import { ArrowLeft, ArrowRight, Save } from '@tamagui/lucide-icons';
import { Button } from '@unistocks/components/button/button';
import { SubmitButtons, SubmitButtonsProps } from '@unistocks/components/button/submit-buttons';
import { Form } from '@unistocks/components/form/form';
import {
  FormComponentPropsBase,
  FormProps,
  FormRenderFunction,
  FormSchema
} from '@unistocks/components/form/model';
import { Tab, Tabs } from '@unistocks/components/tabs/tabs';
import { t } from '@unistocks/localization';
import { useBackHandler } from '@unistocks/shared/hooks/use-backhandler';
import { forEachChildDeep } from '@unistocks/shared/utils/react-utils';
import { isFunction } from '@unistocks/shared/utils/util';
import { PropsWithRef } from '@unistocks/types';
import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { FieldValues, Path, UseFormReturn, useWatch } from 'react-hook-form';
import { isWeb, useComposedRefs, withStaticProperties } from 'tamagui';
import { z } from 'zod';

/**
 * Creates a schema that only validates the provided field paths. Nested paths are
 * validated through their root field. Cross-field refinements on the original
 * schema are not included.
 */
const pickSchemaFields = (schema: z.ZodType, fieldNames: string[]): z.ZodType => {
  if (!(schema instanceof z.ZodObject)) return schema;

  const shape = schema.shape as Record<string, z.ZodType>;
  const picked: Record<string, z.ZodType> = {};

  for (const name of fieldNames) {
    const root = name.split('.')[0];

    if (shape[root]) picked[root] = shape[root];
  }

  return z.object(picked);
};

const getRootFieldNames = <TFieldValues extends FieldValues = FieldValues>(
  fieldNames: Path<TFieldValues>[]
): Path<TFieldValues>[] =>
  Array.from(new Set(fieldNames.map(name => name.split('.')[0] as Path<TFieldValues>)));

const collectFieldNames = <TFieldValues extends FieldValues = FieldValues>(
  content: React.ReactNode
): Path<TFieldValues>[] => {
  const fieldNames: Path<TFieldValues>[] = [];

  forEachChildDeep(content, child => {
    if (React.isValidElement<FormComponentPropsBase<TFieldValues>>(child) && child.props.name) {
      fieldNames.push(child.props.name);
    }
  });

  return fieldNames;
};

export type FormStepProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues
> = {
  label: string;
  hidden?: boolean;
  children: FormRenderFunction<TFieldValues, TContext, TTransformedValues>;
};

function FormStep<T extends FieldValues = FieldValues>({
  label: _label,
  hidden: _hidden,
  children
}: FormStepProps<T>) {
  return <>{children}</>;
}

export type MultiStepFormProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues
> = Omit<FormProps<TFieldValues, TContext, TTransformedValues>, 'children' | 'customTrigger'> & {
  children: Array<React.ReactElement<FormStepProps<TFieldValues, TContext, TTransformedValues>>>;
  /**
   * Previous step must be valid to proceed to next step
   * @default true
   */
  strict?: boolean;
  /**
   * Behavior when pressing the trigger button.
   * If @strict is true, the behavior is always next.
   * @default 'next'
   */
  submitBehavior?: 'submit' | 'next';
  /**
   * Current step index
   */
  step?: number;
  /**
   * Callback when step changes
   */
  onStepChange?: (step: number) => void;
  /**
   * Callback when the back button is pressed on the first step
   */
  onCancel?: () => void;
  triggerProps?: SubmitButtonsProps;
};

type StepMeta<TFieldValues extends FieldValues = FieldValues> = {
  fields: Path<TFieldValues>[];
  fieldsKey: string;
  valid: boolean;
};

type StepContentProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues
> = {
  step: React.ReactElement<FormStepProps<TFieldValues, TContext, TTransformedValues>>;
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  schema?: FormSchema;
  submit: () => void | Promise<void>;
  onMetaChange: (meta: StepMeta<TFieldValues>) => void;
};

function StepContent<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues
>({
  step,
  form,
  schema,
  submit,
  onMetaChange
}: StepContentProps<TFieldValues, TContext, TTransformedValues>) {
  const content = isFunction(step.props.children)
    ? step.props.children(form, submit as any)
    : step.props.children;

  const fields = collectFieldNames<TFieldValues>(content);
  const roots = getRootFieldNames(fields);
  const fieldsKey = fields.join('|');

  const watchedValues = useWatch<TFieldValues>({ name: roots });
  const values = Object.fromEntries(roots.map((root, index) => [root, watchedValues[index]]));
  const valid = !schema || pickSchemaFields(schema, roots).safeParse(values).success;

  useLayoutEffect(() => {
    onMetaChange({ fields, fieldsKey, valid });
    // `fields` is represented by the stable `fieldsKey` in the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsKey, valid]);

  return <>{content}</>;
}

function StepsContainer<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues
>({
  ref,
  children,
  formId,
  initialValues,
  triggerText,
  triggerIcon,
  isLoading,
  schema,
  strict = true,
  submitBehavior: _submitBehaviorProp = 'next',
  step: stepProp,
  onStepChange,
  onCancel,
  triggerProps,
  ...props
}: MultiStepFormProps<TFieldValues, TContext, TTransformedValues>) {
  const formRef = useRef<UseFormReturn<TFieldValues, TContext, TTransformedValues>>(null);
  const formIdInternal = useId();
  const composedRefs = useComposedRefs(ref, formRef);

  /**
   * Index of the currently active step. Held internally when the `step` prop is omitted.
   */
  const [internalStep, setInternalStep] = useState(0);
  const isStepControlled = stepProp !== undefined;
  const step = isStepControlled ? stepProp : internalStep;

  const [stepMeta, setStepMeta] = useState<StepMeta<TFieldValues>>({
    fields: [],
    fieldsKey: '',
    valid: true
  });

  const setStep = (next: number | ((prev: number) => number)) => {
    const nextStep = isFunction(next) ? next(step) : next;

    if (!isStepControlled) setInternalStep(nextStep);
    onStepChange?.(nextStep);
  };

  const childArray = React.Children.toArray(children) as React.ReactElement<
    FormStepProps<TFieldValues, TContext, TTransformedValues>
  >[];
  const isVisible = (
    child: React.ReactElement<FormStepProps<TFieldValues, TContext, TTransformedValues>>
  ) => !child.props.hidden;
  const firstVisibleStepIndex = childArray.findIndex(isVisible);
  const lastVisibleStepIndex = childArray.findLastIndex(isVisible);
  const isFirst = step === firstVisibleStepIndex;
  const isLast = step === lastVisibleStepIndex;
  const submitBehavior = strict ? 'next' : _submitBehaviorProp;

  /**
   * Trigger form submission with validation
   */
  const triggerSubmit = () => formRef.current?.handleSubmit(props.onSubmit, props.onError)();

  /**
   * Validate the current step fields imperatively (event handler only).
   */
  const validateStep = async (stepIndex = step) => {
    if (!schema) return true;

    return (
      formRef.current?.trigger(stepMeta.fields, {
        shouldFocus: stepIndex === step
      }) ?? false
    );
  };

  const onPreviousPress = () => {
    if (isFirst) return;

    setStep(prev => {
      let next = prev - 1;
      while (next > 0 && childArray[next]?.props.hidden) {
        next--;
      }

      return Math.max(next, firstVisibleStepIndex);
    });
  };

  const onNextPress = async () => {
    if (isLast || submitBehavior === 'submit') {
      // Web submits the form natively, so we don't need to trigger submit on next press
      if (!isWeb || triggerProps?.submitButtonType === 'button') triggerSubmit();
    } else {
      const isValid = await validateStep();

      if (isValid) {
        setStep(prev => {
          let next = prev + 1;

          while (next < childArray.length && childArray[next]?.props.hidden) {
            next++;
          }

          return Math.min(next, lastVisibleStepIndex);
        });
      }
    }
  };

  const onTabChange = (tab: string) => {
    let next = parseInt(tab);
    if (next === step) return;

    if (next > step) {
      while (next < childArray.length && childArray[next]?.props.hidden) {
        next++;
      }
    } else {
      while (next > 0 && childArray[next]?.props.hidden) {
        next--;
      }
    }

    setStep(next);
  };

  useBackHandler(
    useCallback(() => {
      if (isFirst && onCancel) {
        onCancel();
        return true;
      }

      onPreviousPress();
      return true;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFirst, onCancel])
  );

  const renderTabs = (form: UseFormReturn<TFieldValues, TContext, TTransformedValues>) => {
    const isCurrentStepValid = stepMeta.valid;

    const tabs = React.Children.map(children, (child, index) => {
      if (!React.isValidElement(child) || child.props.hidden) return null;

      const isNextStep = index === step + 1;
      const isLaterStep = index > step + 1;
      const isDisabled = strict ? isLaterStep || (isNextStep && !isCurrentStepValid) : false;

      return {
        label: child.props.label ?? index.toString(),
        value: index.toString(),
        disabled: isDisabled
      } as Tab;
    })?.filter(Boolean) as Tab[];

    return (
      <Tabs
        tabs={tabs}
        value={step.toString()}
        onValueChange={onTabChange}
        renderTab={tab => {
          const index = parseInt(tab);

          return (
            <StepContent<TFieldValues, TContext, TTransformedValues>
              key={index}
              step={childArray[index]}
              form={form}
              schema={schema}
              submit={onNextPress}
              onMetaChange={setStepMeta}
            />
          );
        }}
      />
    );
  };

  return (
    <Form
      ref={composedRefs}
      formId={formId ?? formIdInternal}
      schema={schema}
      initialValues={initialValues}
      isLoading={isLoading}
      {...props}
      customTrigger={(form, submit, isSubmitting) => (
        <FormStepsTrigger
          isLast={isLast}
          isSubmitting={isSubmitting}
          submitBehavior={submitBehavior}
          submitButtonText={triggerText}
          submitButtonIcon={triggerIcon}
          onCancel={isFirst ? onCancel : onPreviousPress}
          onSubmit={onNextPress}
          {...triggerProps}
        />
      )}
    >
      {renderTabs}
    </Form>
  );
}

type FormStepsTriggerProps = SubmitButtonsProps &
  PropsWithRef<
    {
      isLast: boolean;
      isSubmitting?: boolean;
      submitBehavior: 'submit' | 'next';
    },
    typeof Button
  >;

const FormStepsTrigger: React.FC<FormStepsTriggerProps> = ({
  ref,
  isLast,
  isSubmitting,
  submitBehavior,
  submitButtonText,
  submitButtonIcon,
  ...triggerProps
}) => {
  const submitText = submitButtonText ?? t('::Save');
  const defaultText = isLast ? submitText : t('::PagerNext');
  const text = submitBehavior === 'submit' ? submitText : defaultText;
  const isSubmitStep = submitBehavior === 'submit' || isLast;

  return (
    <SubmitButtons
      submitButtonRef={ref}
      cancelButtonIcon={ArrowLeft}
      submitButtonText={text}
      submitButtonIcon={isSubmitStep ? (submitButtonIcon ?? Save) : undefined}
      submitButtonIconAfter={!isSubmitStep ? ArrowRight : undefined}
      submitButtonType={isSubmitStep ? 'submit' : 'button'}
      isSubmitting={isSubmitting}
      {...triggerProps}
    />
  );
};

export const MultiStepForm = withStaticProperties(StepsContainer, {
  Step: FormStep
});

StepsContainer.displayName = 'MultiStepForm';
FormStep.displayName = 'FormStep';
FormStepsTrigger.displayName = 'FormStepsTrigger';
